export { untilDestroyed } from '@ngneat/until-destroy';
import { of, throwError, timer } from 'rxjs';
import { catchError, tap, filter, first, map, retryWhen, mergeMap, finalize } from 'rxjs/operators';

/**
 * Creates an RxJS operator that catches errors and returns a default value or the original error.
 *
 * @template T The type of the source observable's emitted values
 * @template R The type of the default value to return on error
 * @param defaultValue Optional default value to return when an error occurs. If not provided, the original error is returned.
 * @returns An RxJS operator that transforms the source observable by handling errors
 * @example
 *
 * this._api.getUserData().pipe(
 *   catchErrorAndReturn()
 * ).subscribe(user => {
 *
 * getUserInfo(): Observable<any> {
 *   return this._static.item({slug: 'some-slug'}).pipe(
 *     catchErrorAndReturn({ data: { result: { info: {} } } }),
 *     tap(e => {
 *
 * this._api.getUserData().pipe(
 *   catchErrorAndReturn(
 *     { name: 'Guest', role: 'visitor' },
 *     error => this._toastMessage.error('Failed to load user data')
 *   )
 */
function catchErrorAndReturn(defaultValue = null, errorCallback) {
  return source => {
    return source.pipe(catchError(error => {
      // Execute the error callback if provided
      if (errorCallback) {
        errorCallback(error);
      }
      // Return the default value
      return of(defaultValue ? defaultValue : error);
    }));
  };
}

/**
 * Creates an RxJS operator that executes a callback only once for the first emitted value.
 *
 * @template T The type of values in the source Observable
 * @param callback A function to be called with the first emitted value
 * @returns An RxJS operator that ensures the callback is executed only on the first emission
 * @example
 * this.someStream$.pipe(
 *     effectOnce(() => console.log('Some stream emitted!')),
 *   ).subscribe();
 */
function effectOnce(callback) {
  let executed = false;
  return source => {
    return source.pipe(tap(value => {
      if (!executed) {
        executed = true;
        callback(value);
      }
    }));
  };
}

/**
 * Custom RxJS operator that combines filter and first operators.
 * It filters values based on the provided predicate and then takes only the first value.
 *
 * @param predicate A function that evaluates each value emitted by the source Observable.
 * @returns An Observable that emits only the first value that passes the predicate test.
 *
 * @example
 * public someMethod() {
 *   this._static.item({slug: 'some-slug'}).pipe(
 *     filterFirst(e => !!e)
 *   ).subscribe((e) => {
 *     console.log(e);
 *   });
 * }
 */
function filterFirst(predicate) {
  return source => {
    return source.pipe(filter(predicate), first());
  };
}

/**
 * Creates an RxJS operator that filters out null and undefined values from an Observable stream.
 *
 * @returns An operator that transforms an Observable of nullable values into an Observable of non-nullable values.
 * @typeParam T The type of values in the Observable stream.
 * @example
 *  this._static.item({slug: 'some-slug'}).pipe(
 *    filterNotNull(),
 *    map(data => data[0]),
 */
function filterNotNull() {
  return source => {
    return source.pipe(filter(value => value !== null && value !== undefined));
  };
}

/**
 * Creates an RxJS operator that logs the value of an Observable with an optional label.
 *
 * @template T The type of values emitted by the source Observable
 * @param {string} [label='Value'] - A custom label to prefix the logged value
 * @returns {(source: Observable<T>) => Observable<T>} An Observable operator that logs each value
 * @example
 * source$.pipe(logValue('User data'))
 */
function logValue(label = 'Value') {
  return source => {
    return source.pipe(tap(value => console.log(`${label}:`, value)));
  };
}

/**
 * Maps each emitted value to the specified property or nested property path of that value.
 * Supports both dot notation ('data.result.info') and individual property access.
 *
 * @param propertyPath Property name or dot-separated path to nested property
 * @param defaultValue Value to return if the property is null or undefined (default: null)
 * @returns An Observable that emits the value of the specified property or the default value
 * @example
 * this._api.getSettings().pipe(
 *   mapToProperty('data.result.theme')
 *
 * this._api.getItems().pipe(
 *   mapToProperty('data.result.items', [])
 *
 * this._api.getUserData().pipe(
 *   mapToProperty('data.result.user', {})
 *
 * this._api.getConfig().pipe(
 *   mapToProperty('data.pagination.limit', 10)
 */
function mapToProperty(propertyPath, defaultValue = null) {
  return source => {
    return source.pipe(map(value => {
      if (!value) return defaultValue;
      // Handle dot notation for nested properties
      if (propertyPath.includes('.')) {
        const result = propertyPath.split('.').reduce((obj, prop) => {
          return obj && obj[prop] !== undefined ? obj[prop] : undefined;
        }, value);
        return result !== undefined ? result : defaultValue;
      }
      // Handle single property
      const result = value[propertyPath];
      return result !== undefined ? result : defaultValue;
    }));
  };
}

/**
 * Creates an RxJS operator that retries an observable with exponential backoff
 *
 * @param maxRetries Maximum number of retry attempts (default: 3)
 * @param initialDelay Initial delay between retry attempts in milliseconds (default: 300)
 * @param maxDelay Maximum delay between retry attempts in milliseconds (default: 3000)
 * @param backoffFactor Factor by which the delay increases with each retry attempt (default: 2)
 * @returns An RxJS operator that can be used in a pipe to retry an observable with exponential backoff
 *
 * @example
 * this._api.userPaymentInfo().pipe(
 *   retryWithBackoff(3, 500, 5000),
 *   first()
 * )
 */
function retryWithBackoff(maxRetries = 3, initialDelay = 300, maxDelay = 3000, backoffFactor = 2) {
  return source => {
    return source.pipe(retryWhen(errors => errors.pipe(mergeMap((error, i) => {
      const retryAttempt = i + 1;
      if (retryAttempt > maxRetries) {
        return throwError(() => error);
      }
      const delay = Math.min(maxDelay, initialDelay * Math.pow(backoffFactor, retryAttempt));
      return timer(delay);
    }))));
  };
}

/**
 * Creates an RxJS operator that conditionally taps into an Observable stream
 * and executes a callback only when a predicate condition is met.
 *
 * @template T The type of values in the Observable stream
 * @param predicate A function that tests each value and determines if the callback should be executed
 * @param callback A function to be called when the predicate returns true for a value
 * @returns An RxJS operator that passes through the original Observable values
 * @example
 * public fetchAllUserData() {
 *   const observable = this.getUserInfo(true).pipe(
 *     tapIf(
 *       info => info?.data?.result?.info?.loggedIn,
 *       info => console.log('User is logged in:', info?.data?.result?.info?.player?.id)
 *     ),
 *     switchMap(r => {
 */
function tapIf(predicate, callback) {
  return source => {
    return source.pipe(tap(value => {
      if (predicate(value)) {
        callback(value);
      }
    }));
  };
}

/**
 * Manages loading state for an observable stream.
 * Sets loading state to true before operation starts and to false when it completes or errors.
 *
 * @param setLoading Function to set loading state
 * @example
 * this._api.userInfo().pipe(
 *   withLoading(isLoading => this.isLoading = isLoading),
 *   first(),
 *   tap(e => {
 */
function withLoading(setLoading) {
  return source => {
    return source.pipe(tap(() => setLoading(true)), finalize(() => setLoading(false)));
  };
}

/**
 * Generated bundle index. Do not edit.
 */

export { catchErrorAndReturn, effectOnce, filterFirst, filterNotNull, logValue, mapToProperty, retryWithBackoff, tapIf, withLoading };
